import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Swal from "sweetalert2";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useLanguage } from "../../context/LanguageContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const PAGE_SIZE = 100;
const LOCALE = { es: "es-ES", en: "en-US", ta: "ta-IN" };

const AdminEvents = () => {
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [learners, setLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  // `t` cambia de identidad en cada render; usarlo como dependencia de los loaders
  // re-dispara el useEffect en bucle. Se lee desde un ref.
  const tRef = useRef(t);
  tRef.current = t;

  const showError = (err) => Swal.fire({ icon: "error", title: tRef.current("errorTitle"), text: err.message });

  const authHeaders = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const fetchJson = useCallback(async (url) => {
    const res = await fetch(url, { headers: await authHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error");
    return data;
  }, [authHeaders]);

  const loadLearners = useCallback(async () => {
    setLoadingLearners(true);
    try {
      const data = await fetchJson(`${API_BASE}/api/admin/events`);
      setLearners(data.learners || []);
    } catch (err) {
      showError(err);
    } finally {
      setLoadingLearners(false);
    }
  }, [fetchJson]);

  const loadEvents = useCallback(async (uid, before = null) => {
    setLoadingEvents(true);
    try {
      const qs = `uid=${encodeURIComponent(uid)}&limit=${PAGE_SIZE}${before ? `&before=${encodeURIComponent(before)}` : ""}`;
      const data = await fetchJson(`${API_BASE}/api/admin/events?${qs}`);
      setEvents((prev) => (before ? [...prev, ...(data.events || [])] : data.events || []));
      setNextBefore(data.nextBefore || null);
    } catch (err) {
      showError(err);
    } finally {
      setLoadingEvents(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    if (!adminLoading && isAdmin) loadLearners();
  }, [adminLoading, isAdmin, loadLearners]);

  const selectLearner = (l) => {
    setSelected(l);
    setEvents([]);
    setNextBefore(null);
    setNameFilter("");
    loadEvents(l.uid);
  };

  const fmtDate = (ms) =>
    ms ? new Date(ms).toLocaleString(LOCALE[language] || "es-ES", { dateStyle: "short", timeStyle: "medium" }) : "—";

  const learnerLabel = (l) => l.learnerName || l.username || l.email || l.uid;

  const filteredLearners = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return learners;
    return learners.filter((l) =>
      [l.learnerName, l.username, l.email, l.uid].some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [learners, search]);

  const eventNames = useMemo(
    () => Array.from(new Set(events.map((e) => e.name).filter(Boolean))).sort(),
    [events]
  );
  const visibleEvents = nameFilter ? events.filter((e) => e.name === nameFilter) : events;

  const renderPayload = (p) => {
    if (p == null) return <span className="text-muted">—</span>;
    if (typeof p !== "object") return <span className="small">{String(p)}</span>;
    return Object.entries(p).map(([k, v]) => (
      <span key={k} className="ev-chip">
        <b>{k}:</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}
      </span>
    ));
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 1200 }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <h2 className="mb-0">{t("adminEventsTitle")}</h2>
            {isAdmin && (
              <button className="ev-btn ev-btn-ghost" disabled={loadingLearners} onClick={loadLearners}>
                {t("eventsRefresh")}
              </button>
            )}
          </div>

          {adminLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : !isAdmin ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("notAuthorized")}</Card.Body>
            </Card>
          ) : (
            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
                  <Card.Body>
                    <div className="fw-semibold mb-2">
                      {t("eventsLearners")} {!loadingLearners && <Badge bg="secondary">{learners.length}</Badge>}
                    </div>
                    <Form.Control
                      size="sm"
                      className="mb-2"
                      placeholder={t("eventsSearch")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {loadingLearners ? (
                      <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                    ) : filteredLearners.length === 0 ? (
                      <div className="text-muted small py-3">{t("eventsNoLearners")}</div>
                    ) : (
                      <div className="ev-list">
                        {filteredLearners.map((l) => (
                          <button
                            key={l.uid}
                            className={`ev-learner ${selected?.uid === l.uid ? "active" : ""}`}
                            onClick={() => selectLearner(l)}
                          >
                            <div className="fw-semibold text-truncate">{learnerLabel(l)}</div>
                            {l.email && l.email !== learnerLabel(l) && (
                              <div className="small text-muted text-truncate">{l.email}</div>
                            )}
                            <div className="small text-muted">
                              {t("eventsLastEvent")}: {fmtDate(l.lastEvent?.tsMs)}
                              {l.lastEvent?.name ? ` · ${l.lastEvent.name}` : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>

              <div className="col-12 col-lg-8">
                <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
                  <Card.Body>
                    {!selected ? (
                      <div className="text-muted text-center py-5">{t("eventsPickLearner")}</div>
                    ) : (
                      <>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                          <div>
                            <div className="fw-semibold">{learnerLabel(selected)}</div>
                            <div className="small text-muted">{selected.uid}</div>
                          </div>
                          <Form.Select
                            size="sm"
                            style={{ maxWidth: 220 }}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                          >
                            <option value="">{t("eventsFilterName")}</option>
                            {eventNames.map((n) => <option key={n} value={n}>{n}</option>)}
                          </Form.Select>
                        </div>

                        {loadingEvents && events.length === 0 ? (
                          <div className="text-center py-5"><Spinner animation="border" /></div>
                        ) : events.length === 0 ? (
                          <div className="text-muted text-center py-5">{t("eventsNone")}</div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <Table size="sm" hover className="align-middle mb-2">
                                <thead>
                                  <tr>
                                    <th style={{ whiteSpace: "nowrap" }}>{t("eventsWhen")}</th>
                                    <th>{t("eventsName")}</th>
                                    <th>{t("eventsPlatform")}</th>
                                    <th>{t("eventsDetail")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleEvents.map((e) => (
                                    <tr key={e.id}>
                                      <td className="small" style={{ whiteSpace: "nowrap" }}>{fmtDate(e.tsMs)}</td>
                                      <td><Badge bg="primary">{e.name || t("eventsNoName")}</Badge></td>
                                      <td className="small">{e.platform || "—"}</td>
                                      <td>{renderPayload(e.payload)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                            {nextBefore && (
                              <div className="text-center">
                                <button
                                  className="ev-btn ev-btn-primary"
                                  disabled={loadingEvents}
                                  onClick={() => loadEvents(selected.uid, nextBefore)}
                                >
                                  {loadingEvents ? <Spinner animation="border" size="sm" /> : t("eventsLoadMore")}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      <style>{`
        .ev-btn { display:inline-flex; align-items:center; gap:6px; height:auto !important; width:auto !important;
          padding:8px 16px; border-radius:10px; font-size:.875rem; font-weight:600; border:1.5px solid transparent;
          cursor:pointer; }
        .ev-btn:disabled { opacity:.6; }
        .ev-btn-primary { background:#0984e3; color:#fff; }
        .ev-btn-ghost { background:#f4f4f6; color:#444; }
        .ev-list { max-height:65vh; overflow-y:auto; display:flex; flex-direction:column; gap:6px; }
        .ev-learner { text-align:left; background:#fafafa; border:1.5px solid #eee; border-radius:10px;
          padding:8px 10px; cursor:pointer; width:100% !important; height:auto !important; }
        .ev-learner:hover { background:#f1f6fd; }
        .ev-learner.active { border-color:#0984e3; background:#eaf3fd; }
        .ev-chip { display:inline-block; background:#f4f4f6; border-radius:6px; padding:1px 6px;
          margin:1px 4px 1px 0; font-size:.78rem; word-break:break-all; }
      `}</style>
    </div>
  );
};

export default AdminEvents;
